import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  fitBottomOffset,
  fitPanelHeight,
  OPEN_LEGACY_BOTTOM_PX,
  resolveDesiredBottomPx,
  WIDGET_MIN_HEIGHT,
  type RuntimeBottomOffset,
} from "../anchor";
import { useWidgetRoot } from "../standalone/WidgetRootContext";

interface ResizeState {
  width: number;
  height: number;
}

const STORAGE_KEY = "kapia_chat_size";
const MIN_WIDTH = 320;
const MIN_HEIGHT = WIDGET_MIN_HEIGHT;
const DEFAULT_WIDTH = 384;
const DEFAULT_HEIGHT = 640;
const WIDGET_ID = "kapia-chat-window";

interface UseResizeOptions {
  /** Default width as percentage of viewport/container (e.g. 30 = 30%) */
  initialWidthPercent?: number;
  /** Default height as percentage of viewport/container (e.g. 80 = 80%) */
  initialHeightPercent?: number;
  /**
   * Bottom gap as a percent of the bounds height, or null for the default. A
   * percent because this hook owns the bounds; pixels would mean two places
   * measuring the viewport.
   */
  bottomOffsetPercent?: number | null;
  /** Host page's runtime `setPlacement({ bottomOffset })` override; outranks the percent. */
  runtimeBottomOffset?: RuntimeBottomOffset | null;
  /** When true, skip localStorage read/write and use container bounds instead of viewport */
  previewMode?: boolean;
  /** Container element ref for calculating bounds in preview mode */
  containerRef?: React.RefObject<HTMLElement | null>;
}

/** Convert a percentage (1–100) of the bounds to pixels. */
function percentToPixels(
  percent: number | undefined | null,
  boundsSize: number,
  minPx: number,
  defaultPx: number,
): number {
  if (percent === undefined || percent === null || percent <= 0) return defaultPx;
  return Math.max(minPx, Math.round((boundsSize * percent) / 100));
}

export function useResize(options?: UseResizeOptions) {
  const widthPercent = options?.initialWidthPercent;
  const heightPercent = options?.initialHeightPercent;
  const bottomOffsetPercent = options?.bottomOffsetPercent ?? null;
  const runtimeBottomOffset = options?.runtimeBottomOffset ?? null;
  const previewMode = options?.previewMode ?? false;
  const containerRef = options?.containerRef;

  // The widget's DOM root: the shadow root when embedded, else `document`.
  // `getElementById` must run against this so it resolves elements inside the
  // shadow tree (a plain `document.getElementById` cannot pierce it).
  const widgetRoot = useWidgetRoot();

  /** Container bounds in preview, viewport in production; not for use during render. */
  const getBoundsSize = useCallback(() => {
    if (typeof window === "undefined") return { width: DEFAULT_WIDTH * 3, height: DEFAULT_HEIGHT * 2 };
    if (previewMode && containerRef?.current) {
      return { width: containerRef.current.clientWidth, height: containerRef.current.clientHeight };
    }
    return { width: window.innerWidth, height: window.innerHeight };
  }, [previewMode, containerRef]);

  // Compute effective pixel defaults from percentages.
  // During render we use viewport size (always available); the effect below
  // corrects for container size in preview mode once the ref is mounted.
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : DEFAULT_WIDTH * 3;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : DEFAULT_HEIGHT * 2;
  const effectiveDefaultWidth = percentToPixels(widthPercent, viewportWidth, MIN_WIDTH, DEFAULT_WIDTH);
  // The gap the panel has to sit above. Recomputed by `refit` against the real
  // bounds once mounted; at render time the viewport is the best guess.
  const initialBottomPx = fitBottomOffset(
    resolveDesiredBottomPx(runtimeBottomOffset, undefined, bottomOffsetPercent, viewportHeight, OPEN_LEGACY_BOTTOM_PX),
    viewportHeight,
  );
  const effectiveDefaultHeight = fitPanelHeight(
    percentToPixels(heightPercent, viewportHeight, MIN_HEIGHT, DEFAULT_HEIGHT),
    initialBottomPx,
    viewportHeight,
  );

  // Bounds as state: the resolver needs the height, and nothing else in the
  // widget re-renders on a viewport change today.
  const [bounds, setBounds] = useState<ResizeState>(() => ({
    width: viewportWidth,
    height: viewportHeight,
  }));

  // The window the widget's breakpoints read (the page's, or a preview's
  // iframe), known on the first render so the panel never slides into place.
  const drawnIn = (widgetRoot.ownerDocument ?? (widgetRoot as Document)).defaultView ?? window;
  const [windowWidth, setWindowWidth] = useState(() => drawnIn.innerWidth);

  const [size, setSize] = useState<ResizeState>(() => {
    if (typeof window === "undefined") {
      return { width: effectiveDefaultWidth, height: effectiveDefaultHeight };
    }
    if (previewMode) {
      return { width: effectiveDefaultWidth, height: effectiveDefaultHeight };
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return {
          width: Math.max(MIN_WIDTH, parsed.width || effectiveDefaultWidth),
          // A height stored on a taller screen must still clear the gap here.
          height: fitPanelHeight(
            Math.max(MIN_HEIGHT, parsed.height || effectiveDefaultHeight),
            initialBottomPx,
            viewportHeight,
          ),
        };
      } catch {
        return { width: effectiveDefaultWidth, height: effectiveDefaultHeight };
      }
    }
    return { width: effectiveDefaultWidth, height: effectiveDefaultHeight };
  });

  const isResizingRef = useRef(false);
  const resizeDirectionRef = useRef<string | null>(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const startSizeRef = useRef({ width: 0, height: 0 });
  const startPositionRef = useRef<{
    left: number;
    top: number;
    right: number;
    bottom: number;
    anchoredRight?: boolean;
    anchoredBottom?: boolean;
  }>({ left: 0, top: 0, right: 0, bottom: 0 });
  const animationFrameRef = useRef<number | null>(null);
  const currentSizeRef = useRef({ width: size.width, height: size.height });

  const widgetElementRef = useRef<HTMLElement | null>(null);

  // CSS transform scale factor — used to convert screen-space coords to unscaled coords during resize
  const cssScaleRef = useRef(1);

  // Refs for resize state during drag (avoid React rerenders)
  const resizeSizeRef = useRef({ width: 0, height: 0 });

  const localStorageRafRef = useRef<number | null>(null);
  const pendingSizeRef = useRef<ResizeState | null>(null);

  useEffect(() => {
    widgetElementRef.current = widgetRoot.getElementById(WIDGET_ID);

    const observer = new MutationObserver(() => {
      if (!widgetElementRef.current) {
        widgetElementRef.current = widgetRoot.getElementById(WIDGET_ID);
      }
    });

    const observeTarget = widgetRoot instanceof Document ? widgetRoot.body : widgetRoot;
    observer.observe(observeTarget, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [widgetRoot]);

  const scheduleLocalStorageWrite = useCallback((newSize: ResizeState) => {
    if (previewMode) return;

    pendingSizeRef.current = newSize;

    if (localStorageRafRef.current !== null) {
      cancelAnimationFrame(localStorageRafRef.current);
    }

    localStorageRafRef.current = requestAnimationFrame(() => {
      if (pendingSizeRef.current) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingSizeRef.current));
        pendingSizeRef.current = null;
      }
      localStorageRafRef.current = null;
    });
  }, [previewMode]);

  useEffect(() => {
    if (previewMode) return;

    // Only save if not currently resizing (resize will save on mouseUp)
    if (!isResizingRef.current) {
      scheduleLocalStorageWrite(size);
    }

    return () => {
      if (localStorageRafRef.current !== null) {
        cancelAnimationFrame(localStorageRafRef.current);
        localStorageRafRef.current = null;
      }
    };
  }, [size, scheduleLocalStorageWrite, previewMode]);

  const getCursorForDirection = (direction: string): string => {
    const cursors: Record<string, string> = {
      n: "n-resize",
      s: "s-resize",
      e: "e-resize",
      w: "w-resize",
      ne: "ne-resize",
      nw: "nw-resize",
      se: "se-resize",
      sw: "sw-resize",
    };
    return cursors[direction] || "default";
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, direction: string) => {
      e.preventDefault();
      e.stopPropagation();
      isResizingRef.current = true;
      resizeDirectionRef.current = direction;
      startPosRef.current = { x: e.clientX, y: e.clientY };
      startSizeRef.current = { width: size.width, height: size.height };

      // Always get fresh widget element (it may have been unmounted/remounted)
      const widgetElement = widgetRoot.getElementById(WIDGET_ID);
      if (widgetElement) {
        widgetElementRef.current = widgetElement;
        // Flag the active drag on the widget element (inside the shadow tree)
        // so the resize handles stay visible — a body-level class cannot reach
        // shadow-scoped selectors.
        widgetElement.classList.add("kapia-resizing");
        const rect = widgetElement.getBoundingClientRect();

        let boundsWidth: number;
        let boundsHeight: number;
        let boundsLeft = 0;
        let boundsTop = 0;

        if (previewMode && containerRef?.current) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const clientH = containerRef.current.clientHeight;
          // Compute CSS transform scale: getBoundingClientRect returns scaled size,
          // clientHeight returns the unscaled CSS size
          cssScaleRef.current = clientH > 0 ? containerRect.height / clientH : 1;
          boundsWidth = containerRef.current.clientWidth;
          boundsHeight = clientH;
          boundsLeft = containerRect.left;
          boundsTop = containerRect.top;
        } else {
          cssScaleRef.current = 1;
          boundsWidth = window.innerWidth;
          boundsHeight = window.innerHeight;
        }

        const cssScale = cssScaleRef.current;

        const relativeLeft = (rect.left - boundsLeft) / cssScale;
        const relativeTop = (rect.top - boundsTop) / cssScale;
        const relativeRight = boundsWidth - relativeLeft - rect.width / cssScale;
        const relativeBottom = boundsHeight - relativeTop - rect.height / cssScale;

        const distanceToRight = relativeRight;
        const distanceToLeft = relativeLeft;
        const isAnchoredRight = distanceToRight < distanceToLeft;

        startPositionRef.current = {
          left: relativeLeft,
          top: relativeTop,
          right: relativeRight,
          bottom: relativeBottom,
          anchoredRight: isAnchoredRight,
          anchoredBottom: true,
        };
      }

      resizeSizeRef.current = { width: size.width, height: size.height };

      // Disable text selection on the host page during the drag. Set directly
      // on body (inherited into the shadow tree) since a stylesheet rule
      // injected into the shadow root cannot target the host `<body>`.
      document.body.style.userSelect = "none";
      document.body.style.setProperty("-webkit-user-select", "none");
      document.body.style.cursor = getCursorForDirection(direction);
    },
    [size, previewMode, containerRef, widgetRoot],
  );

  useEffect(() => {
    currentSizeRef.current = { width: size.width, height: size.height };
  }, [size]);

  /**
   * Re-measure the bounds and shrink the panel if it no longer clears the gap.
   * The gap is computed, not read from the DOM: the container is
   * `position: relative`, so its computed `bottom` is always `0px`.
   */
  const refit = useCallback(() => {
    setWindowWidth(drawnIn.innerWidth);
    const next = getBoundsSize();
    if (next.width <= 0 || next.height <= 0) return;

    setBounds((prev) =>
      prev.width === next.width && prev.height === next.height ? prev : next,
    );

    const bottomPx = fitBottomOffset(
      resolveDesiredBottomPx(runtimeBottomOffset, undefined, bottomOffsetPercent, next.height, OPEN_LEGACY_BOTTOM_PX),
      next.height,
    );
    setSize((prev) => {
      const height = fitPanelHeight(prev.height, bottomPx, next.height);
      return height === prev.height ? prev : { ...prev, height };
    });
  }, [drawnIn, getBoundsSize, bottomOffsetPercent, runtimeBottomOffset]);

  // Keep the panel fitting its bounds. Runs in preview too: the preview
  // container is responsive, and the offset changes live while an admin edits
  // it.
  useEffect(() => {
    if (typeof window === "undefined") return;

    let rafId: number | null = null;

    const handleWindowResize = () => {
      // Don't fight a drag in progress.
      if (isResizingRef.current) return;
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        refit();
        rafId = null;
      });
    };

    drawnIn.addEventListener("resize", handleWindowResize);
    // Same deferred path as a real resize, so the first fit lands after
    // paint rather than as a setState inside the effect body.
    handleWindowResize();

    return () => {
      drawnIn.removeEventListener("resize", handleWindowResize);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [refit, drawnIn]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current || !resizeDirectionRef.current) return;

      e.preventDefault();

      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Use requestAnimationFrame for smooth updates (no redundant throttling)
      animationFrameRef.current = requestAnimationFrame(() => {
        const cssScale = cssScaleRef.current;
        // Convert screen-space mouse deltas to unscaled virtual-viewport pixels
        const deltaX = (e.clientX - startPosRef.current.x) / cssScale;
        const deltaY = (e.clientY - startPosRef.current.y) / cssScale;
        const direction = resizeDirectionRef.current;

        if (!direction) {
          animationFrameRef.current = null;
          return;
        }

        // Always get fresh element reference (may have been remounted)
        const widgetElement = widgetRoot.getElementById(WIDGET_ID);
        if (!widgetElement) {
          animationFrameRef.current = null;
          return;
        }

        let newWidth = startSizeRef.current.width;
        let newHeight = startSizeRef.current.height;

        let boundsWidth: number;
        let boundsHeight: number;
        let boundsLeft = 0;
        let boundsTop = 0;

        if (previewMode && containerRef?.current) {
          const containerRect = containerRef.current.getBoundingClientRect();
          boundsWidth = containerRef.current.clientWidth;
          boundsHeight = containerRef.current.clientHeight;
          boundsLeft = containerRect.left;
          boundsTop = containerRect.top;
        } else {
          boundsWidth = window.innerWidth;
          boundsHeight = window.innerHeight;
        }

        if (direction.includes("e")) {
          const rect = widgetElement.getBoundingClientRect();
          const relativeLeft = (rect.left - boundsLeft) / cssScale;
          const maxWidth = boundsWidth - relativeLeft;
          newWidth = Math.max(MIN_WIDTH, Math.min(maxWidth, startSizeRef.current.width + deltaX));
        }
        if (direction.includes("w")) {
          const rect = widgetElement.getBoundingClientRect();
          const relativeRight = (rect.right - boundsLeft) / cssScale;
          const maxWidth = relativeRight; // Can't expand past left edge of bounds
          const proposedWidth = startSizeRef.current.width - deltaX;
          newWidth = Math.max(MIN_WIDTH, Math.min(maxWidth, proposedWidth));
        }

        if (direction.includes("s")) {
          const rect = widgetElement.getBoundingClientRect();
          const relativeTop = (rect.top - boundsTop) / cssScale;
          const maxHeight = boundsHeight - relativeTop;
          newHeight = Math.max(MIN_HEIGHT, Math.min(maxHeight, startSizeRef.current.height + deltaY));
        }
        if (direction.includes("n")) {
          // `startPositionRef.bottom` is measured, so it already reflects the
          // resolved gap; the drag only decides the height.
          const maxHeight = fitPanelHeight(
            Number.POSITIVE_INFINITY,
            startPositionRef.current.bottom,
            boundsHeight,
          );
          newHeight = Math.max(
            MIN_HEIGHT,
            Math.min(maxHeight, startSizeRef.current.height - deltaY),
          );
        }

        resizeSizeRef.current = { width: newWidth, height: newHeight };

        // Update React state during resize to maintain proper anchoring
        // Use startTransition to mark as low-priority (won't block resize animation)
        startTransition(() => {
          setSize({ width: newWidth, height: newHeight });
        });

        animationFrameRef.current = null;
      });
    };

    const handleMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        resizeDirectionRef.current = null;

        widgetElementRef.current?.classList.remove("kapia-resizing");
        document.body.style.userSelect = "";
        document.body.style.removeProperty("-webkit-user-select");
        document.body.style.cursor = "";

        scheduleLocalStorageWrite(resizeSizeRef.current);

        if (animationFrameRef.current !== null) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      }
    };

    // Always add listeners, they check isResizingRef internally
    document.addEventListener("mousemove", handleMouseMove, { passive: false });
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      widgetElementRef.current?.classList.remove("kapia-resizing");
      document.body.style.userSelect = "";
      document.body.style.removeProperty("-webkit-user-select");
    };
  }, [scheduleLocalStorageWrite, previewMode, containerRef, widgetRoot]);

  // In preview mode, use clientWidth/clientHeight to get unscaled dimensions
  const getViewportBounds = useCallback(() => {
    if (previewMode && containerRef?.current) {
      return { width: containerRef.current.clientWidth, height: containerRef.current.clientHeight };
    }
    return { width: window.innerWidth, height: window.innerHeight };
  }, [previewMode, containerRef]);

  // Snapped positions use full height (no bottom offset)
  const positionLeftHalf = useCallback(() => {
    const { width, height } = getViewportBounds();
    setSize({
      width: Math.floor(width / 2),
      height: height,
    });
  }, [getViewportBounds]);

  const positionRightHalf = useCallback(() => {
    const { width, height } = getViewportBounds();
    setSize({
      width: Math.floor(width / 2),
      height: height,
    });
  }, [getViewportBounds]);

  const positionTopHalf = useCallback(() => {
    const { width, height } = getViewportBounds();
    setSize({
      width: width,
      height: Math.floor(height / 2),
    });
  }, [getViewportBounds]);

  const positionBottomHalf = useCallback(() => {
    const { width, height } = getViewportBounds();
    setSize({
      width: width,
      height: Math.floor(height / 2),
    });
  }, [getViewportBounds]);

  const positionTopLeft = useCallback(() => {
    const { width, height } = getViewportBounds();
    setSize({
      width: Math.floor(width / 2),
      height: Math.floor(height / 2),
    });
  }, [getViewportBounds]);

  const positionTopRight = useCallback(() => {
    const { width, height } = getViewportBounds();
    setSize({
      width: Math.floor(width / 2),
      height: Math.floor(height / 2),
    });
  }, [getViewportBounds]);

  const positionBottomLeft = useCallback(() => {
    const { width, height } = getViewportBounds();
    setSize({
      width: Math.floor(width / 2),
      height: Math.floor(height / 2),
    });
  }, [getViewportBounds]);

  const positionBottomRight = useCallback(() => {
    const { width, height } = getViewportBounds();
    setSize({
      width: Math.floor(width / 2),
      height: Math.floor(height / 2),
    });
  }, [getViewportBounds]);

  const positionFullScreen = useCallback(() => {
    const { width, height } = getViewportBounds();
    setSize({
      width: width,
      height: height,
    });
  }, [getViewportBounds]);

  const collapseToDefault = useCallback(() => {
    const next = getBoundsSize();
    const bottomPx = fitBottomOffset(
      resolveDesiredBottomPx(runtimeBottomOffset, undefined, bottomOffsetPercent, next.height, OPEN_LEGACY_BOTTOM_PX),
      next.height,
    );
    setSize({
      width: percentToPixels(widthPercent, next.width, MIN_WIDTH, DEFAULT_WIDTH),
      // The configured height only gets what the gap leaves behind.
      height: fitPanelHeight(
        percentToPixels(heightPercent, next.height, MIN_HEIGHT, DEFAULT_HEIGHT),
        bottomPx,
        next.height,
      ),
    });
  }, [getBoundsSize, widthPercent, heightPercent, bottomOffsetPercent, runtimeBottomOffset]);

  return useMemo(
    () => ({
      size,
      bounds,
      windowWidth,
      handleMouseDown,
      positionLeftHalf,
      positionRightHalf,
      positionTopHalf,
      positionBottomHalf,
      positionTopLeft,
      positionTopRight,
      positionBottomLeft,
      positionBottomRight,
      positionFullScreen,
      collapseToDefault,
    }),
    [
      size,
      bounds,
      windowWidth,
      handleMouseDown,
      positionLeftHalf,
      positionRightHalf,
      positionTopHalf,
      positionBottomHalf,
      positionTopLeft,
      positionTopRight,
      positionBottomLeft,
      positionBottomRight,
      positionFullScreen,
      collapseToDefault,
    ],
  );
}
